import { formatCalendarDate } from '@resida/shared/timezone';
import { GUIDE_CATEGORY_LABELS, type GuideBodyState, type GuideMeta } from '../../content/guides';
import { JsonLd } from '../JsonLd';
import { SEO, SITE_NAME, SITE_URL } from '../SEO';
import { Breadcrumb } from './Breadcrumb';
import { CTABanner } from './CTABanner';
import { MarkdownContent } from './MarkdownContent';
import { RelatedFeatures } from './RelatedFeatures';

interface GuideArticleProps {
  guide: GuideMeta;
  /** Always 'loaded' when prerendered; can be loading or failed after a client-side navigation. */
  bodyState: GuideBodyState;
}

function buildArticleSchema(guide: GuideMeta) {
  const url = `${SITE_URL}/guides/${guide.slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.description,
    datePublished: guide.publishedAt,
    dateModified: guide.updatedAt ?? guide.publishedAt,
    author: { '@type': 'Organization', name: guide.author, url: SITE_URL },
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  };
}

function buildFaqSchema(guide: GuideMeta) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: guide.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}

export function GuideArticle({ guide, bodyState }: GuideArticleProps): JSX.Element {
  return (
    <>
      <SEO
        title={guide.title}
        description={guide.description}
        path={`/guides/${guide.slug}`}
        type="article"
        publishedTime={guide.publishedAt}
        modifiedTime={guide.updatedAt}
      />
      <JsonLd data={buildArticleSchema(guide)} />
      {guide.faq.length > 0 && <JsonLd data={buildFaqSchema(guide)} />}

      <article data-testid="guide-article" className="pt-12 pb-[var(--landing-section-py)] bg-[var(--landing-bg)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: 'Home', href: '/' },
              { label: 'Guides', href: '/guides' },
              { label: guide.title },
            ]}
          />

          <header className="mb-10">
            <p className="text-sm font-medium uppercase tracking-widest text-[var(--landing-accent-text)] mb-3">
              {GUIDE_CATEGORY_LABELS[guide.category]}
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--landing-text-primary)] leading-tight mb-4">
              {guide.title}
            </h1>
            <p className="text-lg text-[var(--landing-text-muted)] leading-relaxed mb-4">
              {guide.description}
            </p>
            <p data-testid="guide-byline" className="text-sm text-[var(--landing-text-muted)]">
              {guide.author} ·{' '}
              {guide.updatedAt ? (
                <>
                  Updated <time dateTime={guide.updatedAt}>{formatCalendarDate(guide.updatedAt)}</time>
                </>
              ) : (
                <>
                  Published <time dateTime={guide.publishedAt}>{formatCalendarDate(guide.publishedAt)}</time>
                </>
              )}
              {guide.legal && (
                <>
                  {' '}
                  · Last reviewed{' '}
                  <time dateTime={guide.legal.reviewedAt}>{formatCalendarDate(guide.legal.reviewedAt)}</time>
                </>
              )}
            </p>
          </header>

          {bodyState.status === 'loaded' && <MarkdownContent variant="article">{bodyState.body}</MarkdownContent>}
          {bodyState.status === 'loading' && (
            <p data-testid="guide-loading" aria-busy="true" className="text-[var(--landing-text-muted)]">
              Loading…
            </p>
          )}
          {bodyState.status === 'failed' && (
            <p data-testid="guide-load-failed" role="alert" className="text-[var(--landing-text-secondary)]">
              This guide didn&apos;t load.{' '}
              {/* A full page load fetches the prerendered page and the current build's assets. */}
              <a href={`/guides/${guide.slug}`} className="text-[var(--landing-accent-text)] underline">
                Reload the page
              </a>
            </p>
          )}

          {guide.legal && (
            <aside
              data-testid="guide-legal-note"
              className="mt-10 border-l-[3px] border-[var(--landing-border)] pl-4 text-sm text-[var(--landing-text-muted)]"
            >
              This guide is general information, not legal advice. Laws change; check your state&apos;s
              current statute or a local attorney before acting.
            </aside>
          )}

          {guide.faq.length > 0 && (
            <section data-testid="guide-faq" aria-labelledby="guide-faq-heading" className="mt-12">
              <h2 id="guide-faq-heading" className="text-2xl font-bold text-[var(--landing-text-primary)] mb-6">
                Frequently asked questions
              </h2>
              <dl className="space-y-6">
                {guide.faq.map((item) => (
                  <div key={item.question}>
                    <dt className="text-lg font-semibold text-[var(--landing-text-primary)] mb-2">
                      {item.question}
                    </dt>
                    <dd className="text-[var(--landing-text-secondary)] leading-relaxed">{item.answer}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>
      </article>

      <RelatedFeatures slugs={guide.relatedFeatures} />
      <CTABanner />
    </>
  );
}
